import { type Agent, AgentUpdatables } from "@albertine/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import "./AgentPreferences.css";
import { useUpdateAgentProfile } from "../../common/hooks/useAgents";
import { EventEmitter } from "../../EventEmitter";
import { AgentAvatar } from "../Common/Avatar";
import Button from "../Common/Buttons";
import { Flex, FlexInline } from "../Common/Flex";
import { Form } from "../Common/Form/Form";
import Input from "../Common/Input";
import Label from "../Common/Label";
import { List } from "../Common/List";
import Spacer from "../Common/Spacer";
import AgentUploadAvatar from "./AgentUploadAvatar";

type Props = {
    agent: Agent | undefined;
};

const AgentPreferences = function AgentPreferences(props: Props) {
    const { agent } = props;

    const {
        handleSubmit,
        setValue,
        trigger,
        formState: { errors, defaultValues },
        reset,
    } = useForm<AgentUpdatables>({
        resolver: zodResolver(AgentUpdatables),
        defaultValues: {
            onboardingCallReservationUrl: agent?.onboardingCallReservationUrl,
            slackId: agent?.slackId,
        },
    });

    const { mutate: updateProfile, isPending: isSaving } = useUpdateAgentProfile({
        onSuccess: () => {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "info",
                    message: "Profile updated!",
                },
            });
        },
        onError: () => {
            EventEmitter.emit({
                name: "showToastMessage",
                params: {
                    type: "error",
                    message: "Agent information update failed.",
                },
            });
        },
    });

    const onSubmit: SubmitHandler<AgentUpdatables> = (update) => {
        updateProfile({ json: update });
    };

    const onInvalid: SubmitErrorHandler<AgentUpdatables> = () => {
        trigger();
        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                type: "error",
                message: "Agent information update failed. Invalid fields.",
            },
        });
    };

    if (!agent) return null;
    return (
        <List.Tab>
            <List.TabHeader>
                <h3>Preferences</h3>
            </List.TabHeader>
            <div className="agent-preferences">
                <Flex.Row gap="large" className="agent-preferences__form">
                    <FlexInline alignSelf="start">
                        <Flex.Column alignItems="start">
                            <AgentAvatar agentId={agent.id} size="huge" />
                        </Flex.Column>
                    </FlexInline>
                    <Flex.Column gap="medium">
                        <Flex.Row gap="small" justifyContent="space-between">
                            <h2>{agent.name}</h2>
                            <AgentUploadAvatar agent={agent.id} />
                        </Flex.Row>
                        <Flex.Row gap="small" alignItems="center">
                            <Label.Medium.N50>First name:</Label.Medium.N50>
                            <Label.Medium>{agent.firstName}</Label.Medium>
                        </Flex.Row>
                        <Flex.Row gap="small" alignItems="center">
                            <Label.Medium.N50>Email:</Label.Medium.N50>
                            <Label.Medium>{agent.email}</Label.Medium>
                        </Flex.Row>
                        <Flex.Row gap="small" alignItems="center">
                            <Label.Medium.N50>Role:</Label.Medium.N50>
                            <Label.Medium>{agent.role}</Label.Medium>
                        </Flex.Row>

                        <Spacer />

                        <h5>Configuration:</h5>

                        <Form.Base onSubmit={handleSubmit(onSubmit, onInvalid)}>
                            <Form.Inputs>
                                <Flex.Column gap="medium">
                                    <Flex.Column gap="small">
                                        <Label.Medium.N50>Slack ID:</Label.Medium.N50>
                                        <Input.TextField
                                            value={defaultValues?.slackId ?? ""}
                                            isValid={() => !errors.slackId}
                                            placeholder="Slack ID..."
                                            supportingText='Please set a correct slack ID for Slack notifications to work correctly. You can find the Slack ID by opening your Slack profile and in right side panel tap three dots and select "Copy member ID".'
                                            onChange={(value) => {
                                                setValue("slackId", value);
                                                trigger(["slackId"]);
                                            }}
                                        />
                                    </Flex.Column>
                                    <Flex.Column gap="small">
                                        <Label.Medium.N50>
                                            Calendly link:
                                        </Label.Medium.N50>
                                        <Input.TextField
                                            value={
                                                defaultValues?.onboardingCallReservationUrl ??
                                                ""
                                            }
                                            isValid={() =>
                                                !errors.onboardingCallReservationUrl
                                            }
                                            placeholder="Calendly link..."
                                            supportingText="Please check that the Calendly link is correct. It is used to allow members to automatically schedule the onboarding calls."
                                            onChange={(value) => {
                                                setValue(
                                                    "onboardingCallReservationUrl",
                                                    value,
                                                );
                                                trigger([
                                                    "onboardingCallReservationUrl",
                                                ]);
                                            }}
                                        />
                                    </Flex.Column>

                                    <Form.Spacer />
                                </Flex.Column>
                            </Form.Inputs>

                            <Form.Actions>
                                <Button
                                    label="Cancel"
                                    onClick={() => {
                                        reset();
                                    }}
                                />
                                <Button.P50
                                    type="submit"
                                    label="Save"
                                    disabled={Object.keys(errors).length > 0}
                                    showLoadingSpinner={isSaving}
                                />
                            </Form.Actions>
                        </Form.Base>
                    </Flex.Column>
                </Flex.Row>
            </div>
        </List.Tab>
    );
};

export default AgentPreferences;
